<template>
    <div class="download-modal-platform-details-container">
        <div v-if="isGenericLite">
            <p>
                The <strong>Generic Lite</strong> format is a simplified version of the
                <strong>Generic HTML</strong> format.
            </p>
            <br />
            <p>
                It is designed for static banner usage and comes with certain limitations.<br />Key differences from the
                <strong>Generic HTML</strong> format:
            </p>
            <br />
            <ul>
                <li>All JavaScript files are removed</li>
                <li>All inline scripts are removed, including GSAP and clickTag</li>
                <li>
                    The Hoxton tag is removed, and DOM elements matching IDs from the manifest are populated with their
                    corresponding values.
                </li>
            </ul>
            <br />
            <p><strong>Important:</strong> Use Generic Lite only for static banners.</p>
        </div>
        <div v-else-if="isHtml">
            <p>
                <strong>
                    Assemble populates raw master templates with the necessary computed values, computed overwrites,
                    metadata and platform specific data and customizations.
                </strong>
            </p>
            <br />
            <p><strong>Changes made to the Hoxton data:</strong></p>
            <ol>
                <li>Set the 'platform' value with the one requested</li>
                <li>
                    Editable values in the manifest data are replaced with updated ones from the overwrites. Only
                    editables already present in the original manifest will be updated
                </li>
                <li>
                    If custom styling exists for the editables, we add it in the Hoxton data as well as add it as
                    'style' to the targeted elements
                </li>
                <li v-if="platform === 'flashtalking'">
                    All the 'editableGroups' from the Hoxton data are removed and editable tag type is converted from
                    'css' to 'text'
                </li>
                <li v-if="platform === 'opendc'">
                    A 'data-placeholder' attribute is added to each editable tag in the template
                </li>
            </ol>
            <br />
            <p><strong>Fonts</strong></p>
            <ol>
                <li>If custom fonts are added, a style tag is added to the head to load them</li>
            </ol>
            <br />
            <p><strong>Files and folders</strong></p>
            <ol>
                <li v-if="platform === 'flashtalking' || platform === 'generic'">
                    The irelevant media queries to the size of the banner are removed from the css file
                </li>
                <li v-if="platform === 'flashtalking' || platform === 'generic'">
                    The width and height properties are added to the body selector in the css file
                </li>
                <li>
                    <code>hoxton.json</code>
                    file is removed
                </li>
                <li>
                    <code>html5-static-wrapper</code>
                    folder is removed if exists
                </li>
                <li v-if="platform === 'opendc'">
                    Computed values are mapped to generate a template.json file in the format
                    <code>[computedValue.name]: type</code>
                    , where type can be "text," "image," or "video."
                    <br />
                    E.g.:
                    <code>{ "placeholders": { "text1": "text", "logo": "image", "videoContainer": "video" } }</code>
                </li>
                <li v-if="platform === 'generic'">
                    The exit function is amended with:
                    <code>function (eventName, exitURL) { window.open(window.clickTag); return; }</code>
                </li>
            </ol>
            <br />
            <p v-if="configs.head"><strong>Changes within the head tag</strong></p>
            <ul v-if="configs.head">
                <li v-if="configs.head.add">Tags added:</li>
                <ol v-if="configs.head.add">
                    <li v-for="(item, index) in configs.head.add" :key="index">
                        <code>{{ item }}</code>
                    </li>
                </ol>
                <li v-if="configs.head.replace">Tags added or replaced:</li>
                <ol v-if="configs.head.replace">
                    <li v-for="(item, index) in configs.head.replace" :key="index">
                        <code>{{ item.value }}</code>
                        is added. Existing tags matching the
                        <code>{{ item.selector }}</code>
                        selector will be replaced
                    </li>
                    <li v-if="platform === 'generic'">
                        The exit function is amended with:
                        <code>function (eventName, exitURL) { window.open(window.clickTag); return; }</code>
                    </li>
                </ol>
            </ul>

            <br />
            <p v-if="configs.body"><strong>Changes within the body tag</strong></p>
            <ul v-if="configs.body">
                <li v-if="configs.body.add">Tags added:</li>
                <ol v-if="configs.body.add">
                    <li v-for="(item, index) in configs.body.add" :key="index">
                        <code>{{ item }}</code>
                    </li>
                </ol>
            </ul>
        </div>
        <div v-else-if="templateType === TemplateType.AE && platform === DOWNLOAD_FORMAT.VIDEO">
            <h2>Step 1: The output file is exported from After Effects</h2>
            <br />
            <p>By default, the default preset is used, but you can specify from one of the following options:</p>
            <br />
            <ul>
                <li><strong>Default</strong> - This will render out a video using the default settings.</li>
                <li><strong>H.264 - Match Render Settings - 5 Mbps</strong> - outputs an .mp4 video</li>
                <li><strong>H.264 - Match Render Settings - 15 Mbps</strong> - outputs an .mp4 video</li>
                <li><strong>High Quality</strong> - outputs an .mov video</li>
                <li><strong>High Quality with Alpha</strong> - outputs an .mov video</li>
            </ul>
            <br />
            <h2>Step 2 (optional): The video is processed with ffmpeg</h2>
            <br />
            <p>There are few different post encoding options:</p>
            <ul>
                <li>
                    <strong>Default</strong>
                    <ul>
                        <li>libx264 codec</li>
                        <li>yuv420p pixel format</li>
                        <li>aac audio codec</li>
                        <li>25 fps</li>
                    </ul>
                </li>
                <li>
                    <strong>None</strong> - Bypass the post encoding step. You'll get the original video as produced by
                    After Effects.
                </li>
                <li>
                    <strong>Smallest</strong> - Smallest possible file size, with an acceptable quality using the H.265
                    codec.
                </li>
                <li><strong>Scale</strong> - Scale the video to a desired resolution.</li>
                <li>
                    <strong>Custom</strong> - Define custom encoding settings. Pick an output format and provide custom
                    encoding parameters.
                </li>
            </ul>
            <Divider />
            <h2>Final output format</h2>
            <p>
                The final output file format is defined by the selected post encoding option, or by the After Effects
                export preset if "None" is selected.
            </p>
        </div>
        <p v-else>No changes apply to this platform.</p>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import { config as platformConfig, type PlatformConfig } from "hoxton-shared/utils/platformConfig";
import { DOWNLOAD_FORMAT, ZIP_DOWNLOADS, type DownloadFormatType } from "hoxton-shared/enums/exports";
import type { TemplateType as TemplateTypeType } from "hoxton-types";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";

export default defineComponent({
    props: {
        platform: {
            required: true,
            type: String as PropType<DownloadFormatType>
        },
        templateType: {
            required: false,
            type: String as PropType<TemplateTypeType>
        }
    },
    data() {
        return {
            configs: {} as PlatformConfig,
            TemplateType,
            DOWNLOAD_FORMAT
        };
    },
    computed: {
        isHtml() {
            return Object.values(ZIP_DOWNLOADS).includes(
                this.platform as (typeof ZIP_DOWNLOADS)[keyof typeof ZIP_DOWNLOADS]
            );
        },
        isGenericLite() {
            return this.platform === DOWNLOAD_FORMAT.GENERIC_LITE;
        }
    },
    watch: {
        platform: {
            immediate: true,
            handler(newPlatform: DownloadFormatType) {
                this.updateConfigs(newPlatform);
            }
        }
    },

    methods: {
        updateConfigs(newPlatform: DownloadFormatType) {
            this.configs =
                newPlatform in platformConfig ? platformConfig[newPlatform as keyof typeof platformConfig] : {};
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.download-modal-platform-details-container {
    height: 400px;
    overflow: auto;
    padding-right: 40px;

    code {
        background-color: $grey1;
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: normal;
    }
    ol,
    ul {
        padding-left: 20px;
    }
}
</style>
